import type { FormatDateOptions, IntlShape } from 'react-intl';

interface FormatLocalizedDateTimeParams {
  date: Date | number | string | null | undefined;
  intl: IntlShape;
  timeZone?: string;
  dateOptions?: FormatDateOptions;
  timeOptions?: FormatDateOptions;
}

interface FormatLocalizedDateTimeResult {
  formattedDate: string;
  formattedTime: string;
}

const DEFAULT_RESULT: FormatLocalizedDateTimeResult = {
  formattedDate: '',
  formattedTime: '',
};

const normalizeDate = (
  value: FormatLocalizedDateTimeParams['date'],
): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsedDate = new Date(value);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  return null;
};

export const formatLocalizedDateTime = (
  params: FormatLocalizedDateTimeParams,
): FormatLocalizedDateTimeResult => {
  const { date, intl, timeZone, dateOptions, timeOptions } = params;
  const normalizedDate = normalizeDate(date);

  if (!normalizedDate) {
    return DEFAULT_RESULT;
  }

  const resolvedTimeZone =
    timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const resolvedDateOptions: FormatDateOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...dateOptions,
    timeZone: dateOptions?.timeZone ?? resolvedTimeZone,
  };

  const resolvedTimeOptions: FormatDateOptions = {
    hour: '2-digit',
    minute: '2-digit',
    ...timeOptions,
    timeZone: timeOptions?.timeZone ?? resolvedTimeZone,
  };

  return {
    formattedDate: intl.formatDate(normalizedDate, resolvedDateOptions),
    formattedTime: intl.formatDate(normalizedDate, resolvedTimeOptions),
  };
};
